import { GamePlayScene } from '../scenes/GamePlayScene';
import { Earth } from '../entities/earth/Earth';
import { Tower } from '../entities/towers/Tower';
import { getEquipmentManager } from '../entities/base/UserProfile';
import { t } from '../i18n/lang';
import { IceEarth } from '../entities/earth/IceEarth';
import { VolcanicEarth } from '../entities/earth/VolcanicEarth';
import { CrystalEarth } from '../entities/earth/CrystalEarth';
import { QuantumEarth } from '../entities/earth/QuantumEarth';
import { NeutronEarth } from '../entities/earth/NeutronEarth';

export interface GameState {
  earth: Earth | null;
  energyCredits: number;
  currentLevel: number;
  currentXP: number;
  xpToNextLevel: number;
  monsterKills: number;
  bossKills: number;
  towers: Tower[];
}

export class GamePlayUIManager {
  private scene: GamePlayScene;
  
  // UI Elements
  private earthHealthText: Phaser.GameObjects.Text | null = null;
  private earthStatsText: Phaser.GameObjects.Text | null = null;
  private creditsText: Phaser.GameObjects.Text | null = null;
  private towersCountText: Phaser.GameObjects.Text | null = null;
  private earthLevelText: Phaser.GameObjects.Text | null = null;
  private earthAbilityBtn: Phaser.GameObjects.Text | null = null;
  private earthAbilityCooldownText: Phaser.GameObjects.Text | null = null;
  private earthAbilityTooltip: Phaser.GameObjects.Text | null = null;
  private upgradeEarthBtn: Phaser.GameObjects.Text | null = null;

  // UI Visibility Management
  private uiElements: {
    topBar: Phaser.GameObjects.GameObject[];
    towerPanel: Phaser.GameObjects.GameObject[];
    sideElements: Phaser.GameObjects.GameObject[];
    equipmentStats: Phaser.GameObjects.GameObject[];
  } = {
    topBar: [],
    towerPanel: [],
    sideElements: [],
    equipmentStats: []
  };
  
  private isUIMinimized = false;
  private backButton: Phaser.GameObjects.Container | null = null;
  private settingsButton: Phaser.GameObjects.Container | null = null;
  private toggleUIButton: Phaser.GameObjects.Container | null = null;

  constructor(scene: GamePlayScene) {
    this.scene = scene;
  }

  createGameUI(gameState: GameState): void {
    // Detect mobile orientation and screen size
    const isMobile = this.detectMobileDevice();
    const isPortrait = this.scene.cameras.main.height > this.scene.cameras.main.width;
    
    if (isMobile && isPortrait) {
      this.createMobilePortraitUI(gameState);
    } else {
      this.createDesktopUI(gameState);
    }
    
    this.createControlButtons();
    this.createEarthAbilityButton(gameState.earth);
    this.createEquipmentStatsDisplay();
    this.createGameplayParticleEffects();
  }

  private detectMobileDevice(): boolean {
    const width = this.scene.cameras.main.width;
    const height = this.scene.cameras.main.height;
    
    // Mobile detection based on screen size and aspect ratio
    return (width < 768 || height < 1024) && (height > width * 1.2);
  }

  private createMobilePortraitUI(gameState: GameState): void {
    // Mobile portrait layout: vertical stack design
    this.createMobileTopBar(gameState);
    this.createMobileControlPanel();
  }

  private createDesktopUI(gameState: GameState): void {
    // Original desktop layout
    this.createTopBar(gameState);
  }

  private createMobileTopBar(gameState: GameState): void {
    const width = this.scene.cameras.main.width;
    
    // Compact mobile top bar
    const topBarBg = this.scene.add.graphics();
    topBarBg.fillGradientStyle(0x1a1a2e, 0x1a1a2e, 0x16213e, 0x16213e, 1, 1, 0.95, 0.95);
    topBarBg.fillRect(0, 0, width, 60); // Reduced height for mobile
    topBarBg.lineStyle(2, 0x4a90e2, 0.8);
    topBarBg.strokeRect(0, 58, width, 2);
    topBarBg.setDepth(1000);
    
    this.uiElements.topBar.push(topBarBg);
    
    // Mobile stats layout - horizontal compact
    this.createMobileStatsRow(gameState);
  }

  private createMobileStatsRow(gameState: GameState): void {
    const width = this.scene.cameras.main.width;
    const padding = 10;
    const sectionWidth = (width - padding * 4) / 3;
    
    // Level section (left)
    const levelContainer = this.scene.add.container(padding + sectionWidth/2, 30);
    levelContainer.setDepth(1001);
    
    const levelBg = this.scene.add.graphics();
    levelBg.fillStyle(0x3498db, 0.8);
    levelBg.fillCircle(0, 0, 15);
    levelBg.lineStyle(1, 0x74b9ff, 1);
    levelBg.strokeCircle(0, 0, 15);
    levelContainer.add(levelBg);
    
    const levelText = this.scene.add.text(0, 0, `${gameState.currentLevel}`, {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    levelContainer.add(levelText);
    
    this.uiElements.topBar.push(levelContainer);
    
    // Energy section (center)
    const energyContainer = this.scene.add.container(width/2, 30);
    energyContainer.setDepth(1001);
    
    const energyIcon = this.scene.add.graphics();
    energyIcon.fillStyle(0xf39c12, 1);
    energyIcon.fillCircle(-15, 0, 8);
    energyIcon.lineStyle(1, 0xffeaa7, 1);
    energyIcon.strokeCircle(-15, 0, 8);
    energyContainer.add(energyIcon);
    
    this.creditsText = this.scene.add.text(0, 0, `${gameState.energyCredits}`, {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#f39c12',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    energyContainer.add(this.creditsText);
    
    this.uiElements.topBar.push(energyContainer);
    
    // XP section (right)
    const xpContainer = this.scene.add.container(width - padding - sectionWidth/2, 30);
    xpContainer.setDepth(1001);
    
    const xpProgress = gameState.currentXP / gameState.xpToNextLevel;
    const xpBarBg = this.scene.add.graphics();
    xpBarBg.fillStyle(0x34495e, 1);
    xpBarBg.fillRoundedRect(-30, -4, 60, 8, 4);
    xpContainer.add(xpBarBg);
    
    const xpBarFill = this.scene.add.graphics();
    xpBarFill.fillGradientStyle(0x9b59b6, 0x74b9ff, 0x8e44ad, 0x0984e3, 1);
    xpBarFill.fillRoundedRect(-30, -4, 60 * xpProgress, 8, 4);
    xpContainer.add(xpBarFill);
    
    this.uiElements.topBar.push(xpContainer);
  }

  private createMobileControlPanel(): void {
    const width = this.scene.cameras.main.width;
    const height = this.scene.cameras.main.height;
    
    // Bottom control panel for mobile
    const controlPanel = this.scene.add.graphics();
    controlPanel.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 0.95);
    controlPanel.fillRoundedRect(5, height - 100, width - 10, 90, 10);
    controlPanel.lineStyle(2, 0x74b9ff, 0.8);
    controlPanel.strokeRoundedRect(5, height - 100, width - 10, 90, 10);
    controlPanel.setDepth(1000);
    
    this.uiElements.towerPanel.push(controlPanel);
  }

  createTowerUI(equippedTowers: string[]): void {
    const isMobile = this.detectMobileDevice();
    const isPortrait = this.scene.cameras.main.height > this.scene.cameras.main.width;
    
    if (isMobile && isPortrait) {
      this.createMobileTowerUI(equippedTowers);
    } else {
      this.createDesktopTowerUI(equippedTowers);
    }
  }

  private createMobileTowerUI(equippedTowers: string[]): void {
    const width = this.scene.cameras.main.width;
    const height = this.scene.cameras.main.height;
    
    // Mobile tower buttons in bottom panel
    const towerTypes = [
      { type: 'laser', name: 'Laser', cost: 100, color: 0x00ff00, icon: '⚡' },
      { type: 'rapid', name: 'Rapid', cost: 150, color: 0xff6600, icon: '🔫' },
      { type: 'heavy', name: 'Heavy', cost: 200, color: 0x666666, icon: '💥' },
      { type: 'missile', name: 'Missile', cost: 250, color: 0xff0000, icon: '🚀' },
      { type: 'freeze', name: 'Freeze', cost: 180, color: 0x00ffff, icon: '❄️' }
    ];
    
    const buttonSize = 45;
    const spacing = (width - 20) / equippedTowers.length;
    
    equippedTowers.forEach((towerType, index) => {
      const tower = towerTypes.find(t => t.type === towerType);
      if (tower) {
        this.createMobileTowerButton(tower, index, spacing, buttonSize, height);
      }
    });
  }

  private createMobileTowerButton(tower: any, index: number, spacing: number, buttonSize: number, screenHeight: number): void {
    const buttonX = 20 + (index * spacing);
    const buttonY = screenHeight - 50;
    
    // Mobile tower button container
    const buttonContainer = this.scene.add.container(buttonX, buttonY);
    buttonContainer.setDepth(1002);
    
    this.uiElements.towerPanel.push(buttonContainer);
    
    // Compact mobile button design
    const buttonBg = this.scene.add.graphics();
    buttonBg.fillGradientStyle(tower.color, tower.color * 0.8, tower.color * 0.6, tower.color * 0.7, 0.9);
    buttonBg.fillCircle(0, 0, buttonSize/2);
    buttonBg.lineStyle(2, 0xffffff, 0.9);
    buttonBg.strokeCircle(0, 0, buttonSize/2);
    buttonContainer.add(buttonBg);
    
    // Tower icon
    const towerIcon = this.scene.add.text(0, -5, tower.icon, {
      fontSize: '20px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    buttonContainer.add(towerIcon);
    
    // Cost display
    const costText = this.scene.add.text(0, 8, `${tower.cost}`, {
      fontSize: '10px',
      fontFamily: 'Arial',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    buttonContainer.add(costText);
    
    // Mobile touch interaction
    buttonContainer.setInteractive(
      new Phaser.Geom.Circle(0, 0, buttonSize/2),
      Phaser.Geom.Circle.Contains
    );
    
    buttonContainer.on('pointerdown', () => {
      (this.scene as any).startTowerPlacement(tower.type);
      this.showMobileNotification(`${tower.name} Satellite selected`);
    });
    
    // Mobile haptic feedback
    buttonContainer.on('pointerover', () => {
      this.scene.tweens.add({
        targets: buttonContainer,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 150,
        ease: 'Back.easeOut'
      });
    });
    
    buttonContainer.on('pointerout', () => {
      this.scene.tweens.add({
        targets: buttonContainer,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Back.easeOut'
      });
    });
  }

  private createDesktopTowerUI(equippedTowers: string[]): void {
    // Original desktop tower UI code (existing implementation)
    const height = this.scene.cameras.main.height;
    
    const panelBg = this.scene.add.graphics();
    panelBg.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 0.95);
    panelBg.fillRoundedRect(10, height - 120, 400, 100, 12);
    panelBg.lineStyle(2, 0x74b9ff, 0.8);
    panelBg.strokeRoundedRect(10, height - 120, 400, 100, 12);
    panelBg.setDepth(1000);
    
    this.uiElements.towerPanel.push(panelBg);
    
    const title = this.scene.add.text(210, height - 110, 'SATELLITE ARSENAL', {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#74b9ff',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(1001);
    
    this.uiElements.towerPanel.push(title);
    
    const towerTypes = [
      { type: 'laser', name: 'Laser', cost: 100, color: 0x00ff00, icon: '⚡' },
      { type: 'rapid', name: 'Rapid', cost: 150, color: 0xff6600, icon: '🔫' },
      { type: 'heavy', name: 'Heavy', cost: 200, color: 0x666666, icon: '💥' },
      { type: 'missile', name: 'Missile', cost: 250, color: 0xff0000, icon: '🚀' },
      { type: 'freeze', name: 'Freeze', cost: 180, color: 0x00ffff, icon: '❄️' }
    ];
    
    towerTypes.forEach((tower, index) => {
      if (equippedTowers.includes(tower.type)) {
        this.createTowerButton(tower, index, height);
      }
    });
  }

  private showMobileNotification(message: string): void {
    const width = this.scene.cameras.main.width;
    
    const notification = this.scene.add.text(width/2, 100, message, {
      fontSize: '16px',
      fontFamily: 'Arial',
      color: '#ffffff',
      backgroundColor: '#2c3e50',
      padding: { x: 15, y: 8 }
    }).setOrigin(0.5).setDepth(2500);
    
    this.scene.tweens.add({
      targets: notification,
      alpha: 0,
      y: 80,
      duration: 2000,
      ease: 'Power2.easeOut',
      onComplete: () => notification.destroy()
    });
  }

  createEarthDisplay(earth: Earth, towers: Tower[]): void {
    if (!earth) return;

    this.createEarthVisual(earth);
    this.createEarthHealthBar(earth);
    // this.createEarthStatsPanel(earth, earth.x, earth.y);
    this.createTowersCountDisplay(earth, towers);
    this.createEarthLevelDisplay(earth);
    this.createUpgradeEarthButton(earth);
  }

  private createTowerButton(tower: any, index: number, screenHeight: number): void {
    const startX = 30;
    const startY = screenHeight - 90;
    const spacing = 75;
    
    const buttonX = startX + (index * spacing);
    const buttonY = startY;
    
    // Tower button container
    const buttonContainer = this.scene.add.container(buttonX, buttonY);
    buttonContainer.setDepth(1002);
    
    // Track for visibility system
    this.uiElements.towerPanel.push(buttonContainer);
    
    // Animated background layers
    this.createTowerButtonBackground(buttonContainer, tower);
    
    // Tower icon with effects
    this.createTowerIcon(buttonContainer, tower);
    
    // Tower info display
    this.createTowerInfo(buttonContainer, tower);
    
    // Cost and availability
    this.createTowerCost(buttonContainer, tower);
    
    // Interactive setup
    this.setupTowerButtonInteraction(buttonContainer, tower, index);
  }

  private createTowerButtonBackground(container: Phaser.GameObjects.Container, tower: any): void {
    // Outer glow ring
    const outerGlow = this.scene.add.graphics();
    outerGlow.fillStyle(tower.color, 0.2);
    outerGlow.fillRoundedRect(-30, -30, 60, 60, 12);
    container.add(outerGlow);
    
    // Main button background with gradient
    const buttonBg = this.scene.add.graphics();
    buttonBg.fillGradientStyle(tower.color, tower.color * 0.8, tower.color * 0.6, tower.color * 0.7, 0.9);
    buttonBg.fillRoundedRect(-25, -25, 50, 50, 8);
    buttonBg.lineStyle(2, 0xffffff, 0.9);
    buttonBg.strokeRoundedRect(-25, -25, 50, 50, 8);
    container.add(buttonBg);
    
    // Inner glow
    const innerGlow = this.scene.add.graphics();
    innerGlow.fillStyle(0xffffff, 0.1);
    innerGlow.fillRoundedRect(-22, -22, 44, 44, 6);
    container.add(innerGlow);
    
    // Animated border
    const animatedBorder = this.scene.add.graphics();
    animatedBorder.lineStyle(1, tower.color, 0.8);
    animatedBorder.strokeRoundedRect(-27, -27, 54, 54, 10);
    container.add(animatedBorder);
    
    // Pulsing animation for outer glow
    this.scene.tweens.add({
      targets: outerGlow,
      scaleX: 1.2,
      scaleY: 1.2,
      alpha: 0.1,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Rotating border animation
    this.scene.tweens.add({
      targets: animatedBorder,
      rotation: Math.PI * 2,
      duration: 8000,
      repeat: -1,
      ease: 'Linear'
    });
  }

  private createTowerIcon(container: Phaser.GameObjects.Container, tower: any): void {
    // Icon background circle
    const iconBg = this.scene.add.circle(0, -8, 12, 0x2c3e50, 0.8);
    container.add(iconBg);
    
    // Tower icon
    const icon = this.scene.add.text(0, -8, tower.icon, {
      fontSize: '20px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    container.add(icon);
    
    // Icon glow effect
    const iconGlow = this.scene.add.circle(0, -8, 15, tower.color, 0.3);
    container.add(iconGlow);
    container.add(icon); // Bring icon to front
    
    // Floating animation for icon
    this.scene.tweens.add({
      targets: [icon, iconBg],
      y: -12,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Pulsing glow
    this.scene.tweens.add({
      targets: iconGlow,
      scaleX: 1.3,
      scaleY: 1.3,
      alpha: 0.1,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createTowerInfo(container: Phaser.GameObjects.Container, tower: any): void {
    // Name background
    const nameBg = this.scene.add.graphics();
    nameBg.fillStyle(0x2c3e50, 0.9);
    nameBg.fillRoundedRect(-25, 8, 50, 12, 4);
    container.add(nameBg);
    
    // Tower name
    const nameText = this.scene.add.text(0, 14, tower.name, {
      fontSize: '7px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    container.add(nameText);
    
    // Stats indicators (small dots)
    const statColors = [0xff6b6b, 0x4ecdc4, 0x45b7d1]; // damage, speed, range
    statColors.forEach((color, index) => {
      const statDot = this.scene.add.circle(-15 + (index * 15), 25, 2, color, 0.8);
      container.add(statDot);
      
      // Gentle pulsing for stats
      this.scene.tweens.add({
        targets: statDot,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 1500 + (index * 200),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    });
  }

  private createTowerCost(container: Phaser.GameObjects.Container, tower: any): void {
    // Cost background
    const costBg = this.scene.add.graphics();
    costBg.fillStyle(0x2c3e50, 0.9);
    costBg.fillRoundedRect(-20, 30, 40, 10, 3);
    costBg.lineStyle(1, 0xf39c12, 0.8);
    costBg.strokeRoundedRect(-20, 30, 40, 10, 3);
    container.add(costBg);
    
    // Cost text
    const costText = this.scene.add.text(0, 35, `${tower.cost}`, {
      fontSize: '7px',
      fontFamily: 'Arial',
      color: '#f39c12',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    container.add(costText);
    
    // Cost icon
    const costIcon = this.scene.add.text(-15, 35, '⚡', {
      fontSize: '8px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    container.add(costIcon);
    
    // Gentle glow for cost
    this.scene.tweens.add({
      targets: costIcon,
      alpha: 0.5,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private setupTowerButtonInteraction(container: Phaser.GameObjects.Container, tower: any, index: number): void {
    // Interactive area
    container.setInteractive(
      new Phaser.Geom.Rectangle(-30, -30, 60, 60),
      Phaser.Geom.Rectangle.Contains
    );
    
    // Hover effects
    container.on('pointerover', () => {
      // Scale up animation
      this.scene.tweens.add({
        targets: container,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 200,
        ease: 'Power2'
      });
      
      // Add hover particles
      this.createHoverParticles(container, tower.color);
      
      // Show tooltip
      this.showTowerTooltip(tower, container.x, container.y - 60);
    });
    
    container.on('pointerout', () => {
      // Scale down animation
      this.scene.tweens.add({
        targets: container,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: 'Power2'
      });
      
      // Hide tooltip
      this.hideTowerTooltip();
    });
    
    container.on('pointerdown', () => {
      // Click feedback
      this.scene.tweens.add({
        targets: container,
        scaleX: 0.9,
        scaleY: 0.9,
        duration: 100,
        yoyo: true,
        ease: 'Power2'
      });
      
      // Selection effect
      this.createSelectionEffect(container, tower.color);
      
      // Start tower placement
      (this.scene as any).startTowerPlacement(tower.type);
      
      // Highlight selected tower
      this.highlightSelectedTower(container);
    });
  }

  private createHoverParticles(container: Phaser.GameObjects.Container, color: number): void {
    // Create small particles around the button on hover
    for (let i = 0; i < 5; i++) {
      const angle = (Math.PI * 2 * i) / 5;
      const distance = 35;
      const x = container.x + Math.cos(angle) * distance;
      const y = container.y + Math.sin(angle) * distance;
      
      const particle = this.scene.add.circle(x, y, 2, color, 0.8);
      particle.setDepth(1004);
      
      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * 10,
        y: y + Math.sin(angle) * 10,
        alpha: 0,
        scaleX: 1.5,
        scaleY: 1.5,
        duration: 600,
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy()
      });
    }
  }

  private createSelectionEffect(container: Phaser.GameObjects.Container, color: number): void {
    // Create expanding ring effect on selection
    const selectionRing = this.scene.add.graphics();
    selectionRing.lineStyle(3, color, 0.8);
    selectionRing.strokeCircle(container.x, container.y, 25);
    selectionRing.setDepth(1005);
    
    this.scene.tweens.add({
      targets: selectionRing,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 500,
      ease: 'Power2.easeOut',
      onComplete: () => selectionRing.destroy()
    });
  }

  private showTowerTooltip(tower: any, x: number, y: number): void {
    // Implementation for showing detailed tower info
    // (This would show tower stats, description, etc.)
  }

  private hideTowerTooltip(): void {
    // Implementation for hiding tooltip
  }

  private highlightSelectedTower(selectedButton: Phaser.GameObjects.Container): void {
    // Remove previous highlights
    this.scene.children.list.forEach(child => {
      if (child.getData && child.getData('towerHighlight')) {
        child.destroy();
      }
    });
    
    // Add highlight to selected tower
    const highlight = this.scene.add.graphics();
    highlight.lineStyle(3, 0xffff00, 1);
    highlight.strokeRoundedRect(selectedButton.x - 27, selectedButton.y - 27, 54, 54, 10);
    highlight.setDepth(1003);
    highlight.setData('towerHighlight', true);
    
    // Pulsing highlight
    this.scene.tweens.add({
      targets: highlight,
      alpha: 0.5,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  updateGameUI(gameState: GameState): void {
    this.updateHealthText(gameState.earth);
    this.updateCreditsText(gameState.energyCredits);
    this.updateEarthAbilityCooldown(gameState.earth);
    this.updateXPDisplay(gameState.currentXP, gameState.xpToNextLevel);
    this.updateKillsDisplay(gameState.monsterKills);
    this.updateLevelDisplay(gameState.currentLevel);
  }

  // Getters for UI elements
  get creditsTextElement(): Phaser.GameObjects.Text | null { return this.creditsText; }
  get earthHealthTextElement(): Phaser.GameObjects.Text | null { return this.earthHealthText; }
  get earthStatsTextElement(): Phaser.GameObjects.Text | null { return this.earthStatsText; }
  get towersCountTextElement(): Phaser.GameObjects.Text | null { return this.towersCountText; }
  get earthLevelTextElement(): Phaser.GameObjects.Text | null { return this.earthLevelText; }
  get earthAbilityBtnElement(): Phaser.GameObjects.Text | null { return this.earthAbilityBtn; }
  get earthAbilityCooldownTextElement(): Phaser.GameObjects.Text | null { return this.earthAbilityCooldownText; }

  private createTopBar(gameState: GameState): void {
    const width = this.scene.cameras.main.width;
    
    // Create gradient background for top bar
    const topBarBg = this.scene.add.graphics();
    topBarBg.fillGradientStyle(0x1a1a2e, 0x1a1a2e, 0x16213e, 0x16213e, 1, 1, 0.9, 0.9);
    topBarBg.fillRect(0, 0, width, 80);
    topBarBg.setDepth(1000);
    
    // Add subtle border
    topBarBg.lineStyle(2, 0x4a90e2, 0.8);
    topBarBg.strokeRect(0, 78, width, 2);
    
    // Create modern panels for different stats
    this.createStatsPanel(gameState);
    this.createResourcePanel(gameState);
    this.createProgressPanel(gameState);
  }

  private createStatsPanel(gameState: GameState): void {
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(0x2c3e50, 0.9);
    panelBg.fillRoundedRect(10, 10, 220, 60, 8);
    panelBg.lineStyle(2, 0x3498db, 0.6);
    panelBg.strokeRoundedRect(10, 10, 220, 60, 8);
    panelBg.setDepth(1001);

    // Level indicator with glow
    const levelBg = this.scene.add.graphics();
    levelBg.fillStyle(0x3498db, 0.8);
    levelBg.fillCircle(40, 40, 18);
    levelBg.lineStyle(2, 0x5dade2, 1);
    levelBg.strokeCircle(40, 40, 18);
    levelBg.setDepth(1002);

    const levelText = this.scene.add.text(40, 40, `${gameState.currentLevel}`, {
      fontSize: '18px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(1003);

    // Animated level glow
    this.scene.tweens.add({
      targets: levelBg,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // Level label
    this.scene.add.text(70, 25, 'LEVEL', {
      fontSize: '12px',
      fontFamily: 'Arial',
      color: '#74b9ff',
      fontStyle: 'bold'
    }).setDepth(1002);
    this.scene.add.text(70, 40, `${gameState.currentLevel}`, {
      fontSize: '20px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setDepth(1002);

    // Kills counter
    this.scene.add.text(120, 25, 'KILLS', {
      fontSize: '12px',
      fontFamily: 'Arial',
      color: '#ff7675',
      fontStyle: 'bold'
    }).setDepth(1002);
    this.scene.add.text(120, 40, `${gameState.monsterKills}`, {
      fontSize: '16px',
      fontFamily: 'Arial',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setDepth(1002);

    // Boss kills counter
    this.scene.add.text(170, 25, 'BOSSES', {
      fontSize: '12px',
      fontFamily: 'Arial',
      color: '#f1c40f',
      fontStyle: 'bold'
    }).setDepth(1002);
    this.scene.add.text(170, 40, `${gameState.bossKills}`, {
      fontSize: '16px',
      fontFamily: 'Arial',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setDepth(1002);
  }

  private createResourcePanel(gameState: GameState): void {
    const width = this.scene.cameras.main.width;
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(0x2c3e50, 0.9);
    panelBg.fillRoundedRect(width - 200, 10, 180, 60, 8);
    panelBg.lineStyle(2, 0xf39c12, 0.6);
    panelBg.strokeRoundedRect(width - 200, 10, 180, 60, 8);
    panelBg.setDepth(1001);
    
    // Energy credits with icon
    const energyIcon = this.scene.add.graphics();
    energyIcon.fillStyle(0xf39c12, 1);
    energyIcon.fillCircle(width - 170, 40, 12);
    energyIcon.lineStyle(2, 0xffeaa7, 1);
    energyIcon.strokeCircle(width - 170, 40, 12);
    energyIcon.setDepth(1002);
    
    // Lightning bolt icon
    const bolt = this.scene.add.graphics();
    bolt.fillStyle(0xffeaa7, 1);
    bolt.beginPath();
    bolt.moveTo(width - 175, 35);
    bolt.lineTo(width - 170, 40);
    bolt.lineTo(width - 168, 37);
    bolt.lineTo(width - 165, 45);
    bolt.lineTo(width - 170, 40);
    bolt.lineTo(width - 172, 43);
    bolt.closePath();
    bolt.fillPath();
    bolt.setDepth(1003);
    
    this.scene.add.text(width - 150, 25, 'ENERGY', {
      fontSize: '12px',
      fontFamily: 'Arial',
      color: '#f39c12',
      fontStyle: 'bold'
    }).setDepth(1002);
    
    this.creditsText = this.scene.add.text(width - 150, 40, `${gameState.energyCredits}`, {
      fontSize: '18px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setDepth(1002);
    
    // Pulse animation for energy
    this.scene.tweens.add({
      targets: energyIcon,
      scaleX: 1.2,
      scaleY: 1.2,
      alpha: 0.7,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createProgressPanel(gameState: GameState): void {
    const width = this.scene.cameras.main.width;
    const centerX = width / 2;
    
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(0x2c3e50, 0.9);
    panelBg.fillRoundedRect(centerX - 120, 10, 240, 60, 8);
    panelBg.lineStyle(2, 0x9b59b6, 0.6);
    panelBg.strokeRoundedRect(centerX - 120, 10, 240, 60, 8);
    panelBg.setDepth(1001);
    
    // XP Progress Bar
    this.scene.add.text(centerX, 25, 'EXPERIENCE', {
      fontSize: '12px',
      fontFamily: 'Arial',
      color: '#9b59b6',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(1002);
    
    // XP Bar background
    const xpBarBg = this.scene.add.graphics();
    xpBarBg.fillStyle(0x34495e, 1);
    xpBarBg.fillRoundedRect(centerX - 100, 42, 200, 12, 6);
    xpBarBg.setDepth(1002);
    
    // XP Bar fill
    const xpProgress = gameState.currentXP / gameState.xpToNextLevel;
    const xpBarFill = this.scene.add.graphics();
    xpBarFill.fillGradientStyle(0x9b59b6, 0x8e44ad, 0x74b9ff, 0x0984e3, 1);
    xpBarFill.fillRoundedRect(centerX - 100, 42, 200 * xpProgress, 12, 6);
    xpBarFill.setDepth(1003);
    
    // XP Text
    this.scene.add.text(centerX, 59, `${gameState.currentXP} / ${gameState.xpToNextLevel}`, {
      fontSize: '10px',
      fontFamily: 'Arial',
      color: '#bdc3c7',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(1002);
    
    // Glow effect for XP bar
    const glow = this.scene.add.graphics();
    glow.fillGradientStyle(0x9b59b6, 0x8e44ad, 0x74b9ff, 0x0984e3, 0.3);
    glow.fillRoundedRect(centerX - 102, 40, 204 * xpProgress, 16, 8);
    glow.setDepth(1001);
    
    this.scene.tweens.add({
      targets: glow,
      alpha: 0.1,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createEarthAbilityButton(earth: Earth | null): void {
    if (!earth) return;
    
    const width = this.scene.cameras.main.width;
    const height = this.scene.cameras.main.height;
    
    // Modern ability button container
    const buttonContainer = this.scene.add.container(width - 120, height - 120);
    buttonContainer.setDepth(1500);
    
    // Track for visibility system
    this.uiElements.sideElements.push(buttonContainer);
    
    // Ability button background with gradient
    const buttonBg = this.scene.add.graphics();
    buttonBg.fillGradientStyle(0x27ae60, 0x2ecc71, 0x16a085, 0x1abc9c, 1);
    buttonBg.fillCircle(0, 0, 40);
    buttonBg.lineStyle(3, 0x52c41a, 1);
    buttonBg.strokeCircle(0, 0, 40);
    buttonContainer.add(buttonBg);
    
    // Outer glow ring
    const glowRing = this.scene.add.graphics();
    glowRing.lineStyle(2, 0x52c41a, 0.6);
    glowRing.strokeCircle(0, 0, 50);
    buttonContainer.add(glowRing);
    
    // Ability icon (Earth symbol)
    const abilityIcon = this.scene.add.graphics();
    abilityIcon.fillStyle(0xffffff, 1);
    abilityIcon.fillCircle(0, 0, 12);
    abilityIcon.fillStyle(0x27ae60, 1);
    abilityIcon.fillCircle(0, 0, 8);
    buttonContainer.add(abilityIcon);
    
    // Ability text
    const abilityText = this.scene.add.text(0, 0, '🌍', {
      fontSize: '24px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    buttonContainer.add(abilityText);
    
    // Cooldown overlay
    const cooldownOverlay = this.scene.add.graphics();
    cooldownOverlay.fillStyle(0x000000, 0.7);
    cooldownOverlay.fillCircle(0, 0, 40);
    cooldownOverlay.setVisible(false);
    buttonContainer.add(cooldownOverlay);
    
    // Cooldown text
    this.earthAbilityCooldownText = this.scene.add.text(0, 0, '', {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#ff6b6b',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    buttonContainer.add(this.earthAbilityCooldownText);
    
    // Pulse animation
    this.scene.tweens.add({
      targets: glowRing,
      scaleX: 1.2,
      scaleY: 1.2,
      alpha: 0.3,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Interactive
    buttonContainer.setInteractive(
      new Phaser.Geom.Circle(0, 0, 40), 
      Phaser.Geom.Circle.Contains
    );
    
    buttonContainer.on('pointerover', () => {
      this.scene.tweens.add({
        targets: buttonBg,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 200,
        ease: 'Power2'
      });
      this.showAbilityTooltip(earth);
    });
    
    buttonContainer.on('pointerout', () => {
      this.scene.tweens.add({
        targets: buttonBg,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: 'Power2'
      });
      this.hideAbilityTooltip();
    });
    
    buttonContainer.on('pointerdown', () => {
      if (earth.canUseSpecialAbility()) {
        (this.scene as any).tryActivateEarthAbility();
        
        // Success feedback
        this.scene.tweens.add({
          targets: buttonBg,
          scaleX: 0.9,
          scaleY: 0.9,
          duration: 100,
          yoyo: true,
          ease: 'Power2'
        });
      }
    });
    
    this.earthAbilityBtn = buttonContainer as any;
  }

  private showAbilityTooltip(earth: Earth): void {
    if (this.earthAbilityTooltip) {
      this.earthAbilityTooltip.destroy();
    }
    
    const width = this.scene.cameras.main.width;
    const height = this.scene.cameras.main.height;
    
    const tooltipBg = this.scene.add.graphics();
    tooltipBg.fillStyle(0x2c3e50, 0.95);
    tooltipBg.fillRoundedRect(width - 280, height - 180, 200, 50, 8);
    tooltipBg.lineStyle(2, 0x3498db, 0.8);
    tooltipBg.strokeRoundedRect(width - 280, height - 180, 200, 50, 8);
    tooltipBg.setDepth(2000);
    
    const abilityName = earth.getSpecialAbility();
    const cooldownTime = earth.getAbilityCooldownRemaining();
    const tooltipText = cooldownTime > 0 
      ? `${abilityName}\nCooldown: ${(cooldownTime / 1000).toFixed(1)}s`
      : `${abilityName}\nReady to use!`;
    
    this.earthAbilityTooltip = this.scene.add.text(width - 180, height - 155, tooltipText, {
      fontSize: '12px',
      fontFamily: 'Arial',
      color: '#ffffff',
      align: 'center',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(2001);
  }

  private hideAbilityTooltip(): void {
    if (this.earthAbilityTooltip) {
      this.earthAbilityTooltip.destroy();
      this.earthAbilityTooltip = null;
    }
  }

  private createAbilityTooltip(earth: Earth): void {
    this.earthAbilityTooltip = this.scene.add.text(
      40,
      this.scene.cameras.main.height - 100,
      this.getEarthAbilityDescription(earth),
      {
        fontSize: '16px',
        color: '#fff',
        backgroundColor: '#222',
        padding: { x: 12, y: 8 },
        fontFamily: 'Arial',
        shadow: { offsetX: 2, offsetY: 2, color: '#000', blur: 4, fill: true },
        wordWrap: { width: 320 },
        maxLines: 4,
      }
    ).setVisible(false).setAlpha(0);
    
    this.earthAbilityTooltip.setDepth(1000);

    // Add hover events
    this.earthAbilityBtn!.on('pointerover', () => this.showTooltip());
    this.earthAbilityBtn!.on('pointerout', () => this.hideTooltip());
  }

  private showTooltip(): void {
    if (!this.earthAbilityTooltip || !this.earthAbilityBtn) return;

    const btnBounds = this.earthAbilityBtn.getBounds();
    let tx = btnBounds.x;
    let ty = btnBounds.y - this.earthAbilityTooltip.height - 8;
    
    // Adjust position if out of bounds
    if (tx + this.earthAbilityTooltip.width > this.scene.cameras.main.width - 10) {
      tx = this.scene.cameras.main.width - this.earthAbilityTooltip.width - 10;
    }
    if (tx < 10) tx = 10;
    if (ty < 10) ty = btnBounds.bottom + 8;
    
    this.earthAbilityTooltip.setPosition(tx, ty);
    this.earthAbilityTooltip.setVisible(true);
    
    this.scene.tweens.add({
      targets: this.earthAbilityTooltip,
      alpha: 1,
      duration: 180,
      ease: 'Power1',
    });
  }

  private hideTooltip(): void {
    if (!this.earthAbilityTooltip) return;

    this.scene.tweens.add({
      targets: this.earthAbilityTooltip,
      alpha: 0,
      duration: 180,
      ease: 'Power1',
      onComplete: () => this.earthAbilityTooltip && this.earthAbilityTooltip.setVisible(false),
    });
  }

  private createEarthVisual(earth: Earth): void {
    // Earth visual representation
    this.scene.add.circle(earth.x, earth.y, 50, 0x4CAF50);
  }

  private createEarthHealthBar(earth: Earth): void {
    const centerX = this.scene.cameras.main.centerX;
    const centerY = this.scene.cameras.main.centerY;
    
    // Health bar container
    const healthBarContainer = this.scene.add.container(centerX, centerY - 80);
    healthBarContainer.setDepth(1500);
    
    // Health bar background with gradient
    const healthBarBg = this.scene.add.graphics();
    healthBarBg.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 1);
    healthBarBg.fillRoundedRect(-100, -8, 200, 16, 8);
    healthBarBg.lineStyle(2, 0xe74c3c, 0.8);
    healthBarBg.strokeRoundedRect(-100, -8, 200, 16, 8);
    healthBarContainer.add(healthBarBg);
    
    // Health bar fill
    const healthRatio = earth.getHealth() / earth.getMaxHealth();
    const healthBarFill = this.scene.add.graphics();
    
    // Dynamic color based on health percentage
    let healthColor1, healthColor2;
    if (healthRatio > 0.6) {
      healthColor1 = 0x27ae60; // Green
      healthColor2 = 0x2ecc71;
    } else if (healthRatio > 0.3) {
      healthColor1 = 0xf39c12; // Orange
      healthColor2 = 0xe67e22;
    } else {
      healthColor1 = 0xe74c3c; // Red
      healthColor2 = 0xc0392b;
    }
    
    healthBarFill.fillGradientStyle(healthColor1, healthColor2, healthColor1, healthColor2, 1);
    healthBarFill.fillRoundedRect(-98, -6, 196 * healthRatio, 12, 6);
    healthBarContainer.add(healthBarFill);
    
    // Health text with background
    const healthTextBg = this.scene.add.graphics();
    healthTextBg.fillStyle(0x2c3e50, 0.9);
    healthTextBg.fillRoundedRect(-40, -25, 80, 15, 4);
    healthBarContainer.add(healthTextBg);
    
    this.earthHealthText = this.scene.add.text(0, -17, 
      `${Math.floor(earth.getHealth())} / ${Math.floor(earth.getMaxHealth())}`, {
      fontSize: '12px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    healthBarContainer.add(this.earthHealthText);
    
    // Pulse effect for low health
    if (healthRatio < 0.3) {
      this.scene.tweens.add({
        targets: healthBarFill,
        alpha: 0.6,
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
      
      // Screen flash for critical health
      if (healthRatio < 0.15) {
        const criticalOverlay = this.scene.add.rectangle(
          this.scene.cameras.main.centerX,
          this.scene.cameras.main.centerY,
          this.scene.cameras.main.width,
          this.scene.cameras.main.height,
          0xff0000,
          0.1
        );
        criticalOverlay.setDepth(500);
        
        this.scene.tweens.add({
          targets: criticalOverlay,
          alpha: 0,
          duration: 300,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut'
        });
      }
    }
    
    // Earth stats panel
    // this.createEarthStatsPanel(earth, centerX, centerY);
  }

  private createEarthStatsPanel(earth: Earth, centerX: number, centerY: number): void {
    const statsPanelBg = this.scene.add.graphics();
    statsPanelBg.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 0.95);
    statsPanelBg.fillRoundedRect(centerX - 150, centerY + 80, 300, 60, 10);
    statsPanelBg.lineStyle(2, 0x74b9ff, 0.7);
    statsPanelBg.strokeRoundedRect(centerX - 150, centerY + 80, 300, 60, 10);
    statsPanelBg.setDepth(1400);
    
    // Earth type indicator
    const earthType = this.getEarthTypeName(earth);
    this.scene.add.text(centerX, centerY + 95, earthType, {
      fontSize: '16px',
      fontFamily: 'Arial Black',
      color: '#74b9ff',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(1401);
    
    // Stats display
    const statsText = [
      `Defense: ${(earth.getDefense() * 100).toFixed(0)}%`,
      `Towers: ${earth.getTowerLimit()}`,
      `Level: ${earth.getLevel()}`
    ];
    
    statsText.forEach((stat, index) => {
      this.scene.add.text(centerX - 120 + (index * 80), centerY + 115, stat, {
        fontSize: '10px',
        fontFamily: 'Arial',
        color: '#bdc3c7',
        fontStyle: 'bold'
      }).setOrigin(0, 0.5).setDepth(1401);
    });
    
    // Ability status indicator
    const abilityReady = earth.canUseSpecialAbility();
    const abilityStatusColor = abilityReady ? 0x27ae60 : 0xe74c3c;
    const abilityStatusText = abilityReady ? 'READY' : 'COOLDOWN';
    
    const abilityIndicator = this.scene.add.graphics();
    abilityIndicator.fillStyle(abilityStatusColor, 0.8);
    abilityIndicator.fillCircle(centerX + 120, centerY + 115, 8);
    abilityIndicator.setDepth(1401);
    
    this.scene.add.text(centerX + 135, centerY + 115, abilityStatusText, {
      fontSize: '8px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0, 0.5).setDepth(1401);
  }

  private getEarthTypeName(earth: Earth): string {
    if (earth instanceof IceEarth) return '❄️ ICE EARTH';
    if (earth instanceof VolcanicEarth) return '🔥 VOLCANIC EARTH';
    if (earth instanceof CrystalEarth) return '💎 CRYSTAL EARTH';
    if (earth instanceof QuantumEarth) return '⚛️ QUANTUM EARTH';
    if (earth instanceof NeutronEarth) return '⭐ NEUTRON EARTH';
    return '🌍 CLASSIC EARTH';
  }

  private createTowersCountDisplay(earth: Earth, towers: Tower[]): void {
    this.towersCountText = this.scene.add.text(
      earth.x,
      earth.y + 70,
      `${t('towers')}: ${towers.length}/${earth.getTowerLimit()}`,
      {
        fontSize: '16px',
        color: '#FFD700',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
  }

  private createEarthLevelDisplay(earth: Earth): void {
    this.earthLevelText = this.scene.add.text(
      earth.x,
      earth.y + 100,
      `Earth Lv.${earth.getLevel()}`,
      { fontSize: '15px', color: '#FFD700', fontFamily: 'Arial' }
    ).setOrigin(0.5);
  }

  private createUpgradeEarthButton(earth: Earth): void {
    this.upgradeEarthBtn = this.scene.add.text(
      earth.x + 90,
      earth.y + 100,
      'Upgrade Earth',
      { fontSize: '14px', color: '#fff', backgroundColor: '#4CAF50', padding: { x: 10, y: 4 }, fontFamily: 'Arial' }
    ).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    this.upgradeEarthBtn.on('pointerdown', () => (this.scene as any).upgradeEarth());
    this.upgradeEarthBtn.on('pointerover', () => this.upgradeEarthBtn!.setStyle({ backgroundColor: '#388E3C' }));
    this.upgradeEarthBtn.on('pointerout', () => this.upgradeEarthBtn!.setStyle({ backgroundColor: '#4CAF50' }));
  }

  private createEquipmentStatsDisplay(): void {
    const equipmentStats = getEquipmentManager().calculateTotalStats();
    
    let statsText = 'Equipment Bonuses:\n';
    if (equipmentStats.damage) statsText += `Damage: +${equipmentStats.damage.toFixed(0)}\n`;
    if (equipmentStats.attackSpeed) statsText += `Attack Speed: +${(equipmentStats.attackSpeed * 100).toFixed(1)}%\n`;
    if (equipmentStats.range) statsText += `Range: +${equipmentStats.range.toFixed(0)}\n`;
    if (equipmentStats.critChance) statsText += `Crit Chance: +${(equipmentStats.critChance * 100).toFixed(1)}%\n`;
    if (equipmentStats.earthHealth) statsText += `Earth Health: +${equipmentStats.earthHealth.toFixed(0)}\n`;
    if (equipmentStats.earthDefense) statsText += `Earth Defense: +${(equipmentStats.earthDefense * 100).toFixed(1)}%\n`;
    if (equipmentStats.energyRegen) statsText += `Energy Regen: +${equipmentStats.energyRegen.toFixed(1)}/s\n`;
    if (equipmentStats.goldBonus) statsText += `Gold Bonus: +${(equipmentStats.goldBonus * 100).toFixed(1)}%\n`;
    if (equipmentStats.rubyBonus) statsText += `Ruby Bonus: +${(equipmentStats.rubyBonus * 100).toFixed(1)}%\n`;
    if (equipmentStats.xpBonus) statsText += `XP Bonus: +${(equipmentStats.xpBonus * 100).toFixed(1)}%\n`;
    if (equipmentStats.projectileCount) statsText += `Extra Projectiles: +${equipmentStats.projectileCount}\n`;
    if (equipmentStats.pierceCount) statsText += `Pierce Count: +${equipmentStats.pierceCount}\n`;
    
    if (statsText === 'Equipment Bonuses:\n') {
      statsText = 'No Equipment Bonuses';
    }

    const equipmentDisplay = this.scene.add.text(30, 120, statsText, {
      fontSize: '12px',
      color: '#cccccc',
      backgroundColor: '#000000',
      padding: { x: 8, y: 6 },
      fontFamily: 'Arial',
    });
    
    // Track for visibility system
    this.uiElements.equipmentStats.push(equipmentDisplay);
  }

  // Update methods
  private updateHealthText(earth: Earth | null): void {
    if (this.earthHealthText && earth) {
      this.earthHealthText.setText(`${Math.round(earth.getHealth())}/${earth.getMaxHealth()}`);
    }
  }

  private updateCreditsText(energyCredits: number): void {
    if (this.creditsText) {
      this.creditsText.setText(`${t('credits')}: ${energyCredits}`);
    }
  }

  private updateEarthAbilityCooldown(earth: Earth | null): void {
    if (!this.earthAbilityCooldownText || !earth) return;

    const earthAny = earth as any;
    if (earthAny.lastAbilityTime && earthAny.abilityCooldown) {
      const now = this.scene.time.now;
      const timeLeft = earthAny.abilityCooldown - (now - earthAny.lastAbilityTime);
      if (timeLeft > 0) {
        this.earthAbilityCooldownText.setText(`Cooldown: ${Math.ceil(timeLeft / 1000)}s`);
      } else {
        this.earthAbilityCooldownText.setText('');
        if (this.earthAbilityBtn && this.earthAbilityBtn.setStyle) {
          this.earthAbilityBtn.setStyle({ backgroundColor: '#1976D2' });
          this.earthAbilityBtn.setText(`${this.getEarthAbilityName(earth)} (Ready)`);
        }
      }
    }
  }

  private updateXPDisplay(currentXP: number, xpToNextLevel: number): void {
    const xpDisplay = this.scene.children.list.find(obj =>
      obj instanceof Phaser.GameObjects.Text && (obj as Phaser.GameObjects.Text).text.startsWith(`${t('xp')}`)
    ) as Phaser.GameObjects.Text;
    if (xpDisplay) {
      xpDisplay.setText(`${t('xp')}: ${currentXP}/${xpToNextLevel}`);
    }
  }

  private updateKillsDisplay(monsterKills: number): void {
    const killsDisplay = this.scene.children.list.find(obj =>
      obj instanceof Phaser.GameObjects.Text && (obj as Phaser.GameObjects.Text).text.startsWith(`${t('kills')}`)
    ) as Phaser.GameObjects.Text;
    if (killsDisplay) {
      killsDisplay.setText(`${t('kills')}: ${monsterKills}`);
    }
  }

  private updateLevelDisplay(currentLevel: number): void {
    const levelText = this.scene.children.list.find(obj =>
      obj instanceof Phaser.GameObjects.Text && (obj as Phaser.GameObjects.Text).text.startsWith(`${t('level')}`)
    ) as Phaser.GameObjects.Text;
    if (levelText) {
      levelText.setText(`${t('level')}: ${currentLevel}`);
    }
  }

  // Helper methods
  private getEarthAbilityName(earth: Earth): string {
    return earth.getSpecialAbility();
  }

  private getEarthAbilityDescription(earth: Earth): string {
    switch (this.getEarthAbilityName(earth)) {
      case 'Freeze': return 'Freeze all monsters near Earth for 2.5s.';
      case 'Reflect': return 'Reflect 30% damage to monsters attacking Earth for 4s.';
      case 'GenerateEnergy': return 'Instantly gain 300 energy credits.';
      case 'Teleport': return 'Teleport 1 tower to a new position.';
      case 'GravityField': return 'Slow all monsters by 50% for 3.5s.';
      default: return 'No special ability.';
    }
  }

  private getEarthStatsText(earth: Earth, towers: Tower[]): string {
    return `${t('earth_stats')}:\n` +
           `${t('hp')}: ${earth.getHealth()}/${earth.getMaxHealth()}\n` +
           `${t('dr')}: ${Math.round(earth.getDamageResistance() * 100)}%\n` +
           `${t('towers')}: ${towers.length}/${earth.getTowerLimit()}\n` +
           `${t('ability')}: ${this.getEarthAbilityName(earth)}`;
  }

  private getTowerDisplayName(towerKey: string): string {
    switch (towerKey) {
      case 'laser': return t('laser_tower');
      case 'rapid': return t('rapid_fire_tower');
      case 'heavy': return t('heavy_cannon');
      case 'missile': return 'Missile Tower';
      case 'freeze': return 'Freeze Tower';
      case 'multi': return 'Multi-Directional';
      case 'piercing': return 'Piercing';
      case 'chain': return 'Chain Lightning';
      case 'shotgun': return 'Shotgun';
      case 'beam': return 'Beam';
      default: return towerKey;
    }
  }

  private getTowerColor(towerKey: string): string {
    switch (towerKey) {
      case 'laser': return '#4CAF50';
      case 'rapid': return '#2196F3';
      case 'heavy': return '#8B4513';
      case 'missile': return '#607d8b';
      case 'freeze': return '#00bcd4';
      case 'multi': return '#ff9800';
      case 'piercing': return '#8e24aa';
      case 'chain': return '#80d8ff';
      case 'shotgun': return '#d32f2f';
      case 'beam': return '#ffeb3b';
      default: return '#666666';
    }
  }

  private getTowerHoverColor(towerKey: string): string {
    switch (towerKey) {
      case 'laser': return '#388E3C';
      case 'rapid': return '#1976D2';
      case 'heavy': return '#5D2E0D';
      case 'missile': return '#455a64';
      case 'freeze': return '#0097a7';
      case 'multi': return '#f57c00';
      case 'piercing': return '#6a0080';
      case 'chain': return '#0097a7';
      case 'shotgun': return '#b71c1c';
      case 'beam': return '#ffd600';
      default: return '#444444';
    }
  }

  private getTowerCost(towerType: string): number {
    switch (towerType) {
      case 'laser': return 100;
      case 'rapid': return 150;
      case 'heavy': return 200;
      case 'missile': return 250;
      case 'freeze': return 180;
      case 'multi': return 180;
      case 'piercing': return 220;
      case 'chain': return 260;
      case 'shotgun': return 170;
      case 'beam': return 300;
      default: return 100;
    }
  }

  showNotification(message: string, duration: number = 2000, color: string = '#4CAF50'): void {
    // Create notification container
    const centerX = this.scene.cameras.main.centerX;
    const notificationY = 150;
    
    const notificationContainer = this.scene.add.container(centerX, notificationY);
    notificationContainer.setDepth(3000);
    
    // Notification background with modern styling
    const notificationBg = this.scene.add.graphics();
    
    // Convert hex color to number
    const bgColor = parseInt(color.replace('#', ''), 16);
    
    notificationBg.fillGradientStyle(bgColor, bgColor, bgColor * 0.8, bgColor * 0.8, 0.95);
    notificationBg.fillRoundedRect(-120, -20, 240, 40, 20);
    notificationBg.lineStyle(2, 0xffffff, 0.8);
    notificationBg.strokeRoundedRect(-120, -20, 240, 40, 20);
    
    // Add subtle shadow
    const shadow = this.scene.add.graphics();
    shadow.fillStyle(0x000000, 0.3);
    shadow.fillRoundedRect(-118, -18, 236, 36, 18);
    notificationContainer.add(shadow);
    notificationContainer.add(notificationBg);
    
    // Notification icon based on color
    let icon = '✓';
    if (color === '#ff6b6b' || color === '#e74c3c') icon = '⚠️';
    else if (color === '#ffaa00' || color === '#f39c12') icon = '⚡';
    else if (color === '#74b9ff' || color === '#3498db') icon = 'ℹ️';
    
    const iconText = this.scene.add.text(-100, 0, icon, {
      fontSize: '16px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    notificationContainer.add(iconText);
    
    // Notification text with better typography
    const notificationText = this.scene.add.text(-10, 0, message, {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: 180 }
    }).setOrigin(0.5);
    notificationContainer.add(notificationText);
    
    // Entrance animation
    notificationContainer.setAlpha(0);
    notificationContainer.setScale(0.5);
    
    this.scene.tweens.add({
      targets: notificationContainer,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 300,
      ease: 'Back.easeOut'
    });
    
    // Floating animation
    this.scene.tweens.add({
      targets: notificationContainer,
      y: notificationY - 10,
      duration: 1000,
      yoyo: true,
      repeat: Math.floor(duration / 2000),
      ease: 'Sine.easeInOut'
    });
    
    // Exit animation
    this.scene.time.delayedCall(duration - 500, () => {
      this.scene.tweens.add({
        targets: notificationContainer,
        alpha: 0,
        scaleX: 0.8,
        scaleY: 0.8,
        y: notificationY - 50,
        duration: 500,
        ease: 'Power2.easeIn',
        onComplete: () => {
          notificationContainer.destroy();
        }
      });
    });
    
    // Ripple effect
    const ripple = this.scene.add.graphics();
    ripple.lineStyle(3, 0xffffff, 0.8);
    ripple.strokeCircle(0, 0, 10);
    notificationContainer.add(ripple);
    
    this.scene.tweens.add({
      targets: ripple,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 800,
      ease: 'Power2.easeOut'
    });
  }

  private createGameplayParticleEffects(): void {
    // Add ambient UI particles
    this.createAmbientUIParticles();
    
    // Add energy flow effects
    this.createEnergyFlowEffects();
    
    // Add atmospheric UI elements
    this.createAtmosphericElements();
  }

  private createAmbientUIParticles(): void {
    // Create floating UI particles around interface elements
    const particleSpawner = () => {
      const colors = [0x74b9ff, 0xa29bfe, 0x6c5ce7, 0x00b894];
      const color = colors[Phaser.Math.Between(0, colors.length - 1)];
      
      // Spawn near UI elements
      const spawnAreas = [
        { x: 100, y: 50, width: 200, height: 60 },    // Top left panel
        { x: this.scene.cameras.main.width - 200, y: 50, width: 180, height: 60 }, // Top right panel
        { x: this.scene.cameras.main.width / 2 - 120, y: 50, width: 240, height: 60 }, // Center panel
      ];
      
      const area = spawnAreas[Phaser.Math.Between(0, spawnAreas.length - 1)] as { x: number, y: number, width: number, height: number };
      const x = area.x + Phaser.Math.Between(-20, area.width + 20);
      const y = area.y + Phaser.Math.Between(-10, area.height + 10);
      
      const particle = this.scene.add.circle(x, y, 1, color, 0.6);
      particle.setDepth(999);
      
      // Float upward and fade
      this.scene.tweens.add({
        targets: particle,
        y: y - Phaser.Math.Between(30, 60),
        alpha: 0,
        scaleX: 0.5,
        scaleY: 0.5,
        duration: Phaser.Math.Between(2000, 4000),
        ease: 'Power2.easeOut',
        onComplete: () => particle.destroy()
      });
    };
    
    // Spawn particles continuously
    this.scene.time.addEvent({
      delay: 800,
      callback: particleSpawner,
      loop: true
    });
  }

  private createEnergyFlowEffects(): void {
    // Create energy flow lines between UI elements
    const energyFlow = this.scene.add.graphics();
    energyFlow.setDepth(998);
    
    const createEnergyLine = () => {
      const startX = this.scene.cameras.main.width / 2;
      const startY = 40;
      const endX = this.scene.cameras.main.width - 120;
      const endY = 40;
      
      // Clear previous line
      energyFlow.clear();
      
      // Draw energy flow line
      energyFlow.lineStyle(2, 0x74b9ff, 0.3);
      energyFlow.beginPath();
      energyFlow.moveTo(startX, startY);
      
      // Create wavy line
      const steps = 20;
      for (let i = 1; i <= steps; i++) {
        const progress = i / steps;
        const x = startX + (endX - startX) * progress;
        const y = startY + Math.sin(progress * Math.PI * 2 + this.scene.time.now * 0.005) * 3;
        energyFlow.lineTo(x, y);
      }
      energyFlow.strokePath();
      
      // Add glow points along the line
      for (let i = 0; i < 3; i++) {
        const progress = (i + 1) / 4;
        const x = startX + (endX - startX) * progress;
        const y = startY + Math.sin(progress * Math.PI * 2 + this.scene.time.now * 0.005) * 3;
        
        const glowPoint = this.scene.add.circle(x, y, 2, 0x74b9ff, 0.8);
        glowPoint.setDepth(999);
        
        this.scene.tweens.add({
          targets: glowPoint,
          scaleX: 1.5,
          scaleY: 1.5,
          alpha: 0.3,
          duration: 1000,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut'
        });
      }
    };
    
    // Update energy flow
    this.scene.time.addEvent({
      delay: 100,
      callback: createEnergyLine,
      loop: true
    });
  }

  private createAtmosphericElements(): void {
    // Add subtle atmospheric effects around the UI
    const atmosphereOverlay = this.scene.add.graphics();
    atmosphereOverlay.setDepth(997);
    
    // Create scanning line effect
    const createScanLine = () => {
      const scanLine = this.scene.add.graphics();
      scanLine.lineStyle(1, 0x00ff88, 0.6);
      scanLine.moveTo(0, 0);
      scanLine.lineTo(this.scene.cameras.main.width, 0);
      scanLine.strokePath();
      scanLine.setDepth(996);
      scanLine.y = -5;
      
      this.scene.tweens.add({
        targets: scanLine,
        y: this.scene.cameras.main.height + 5,
        alpha: 0,
        duration: 3000,
        ease: 'Power2.easeOut',
        onComplete: () => scanLine.destroy()
      });
    };
    
    // Create scan lines periodically
    this.scene.time.addEvent({
      delay: 8000,
      callback: createScanLine,
      loop: true
    });
    
    // Add corner decorations
    this.createCornerDecorations();
  }

  private createCornerDecorations(): void {
    const corners = [
      { x: 20, y: 20, angle: 0 },        // Top left
      { x: this.scene.cameras.main.width - 20, y: 20, angle: 90 },  // Top right
      { x: 20, y: this.scene.cameras.main.height - 20, angle: 270 }, // Bottom left
      { x: this.scene.cameras.main.width - 20, y: this.scene.cameras.main.height - 20, angle: 180 } // Bottom right
    ];
    
    corners.forEach(corner => {
      const decoration = this.scene.add.graphics();
      decoration.lineStyle(2, 0x74b9ff, 0.6);
      
      // L-shaped corner decoration
      decoration.moveTo(0, 0);
      decoration.lineTo(15, 0);
      decoration.moveTo(0, 0);
      decoration.lineTo(0, 15);
      decoration.strokePath();
      
      decoration.x = corner.x;
      decoration.y = corner.y;
      decoration.rotation = Phaser.Math.DegToRad(corner.angle);
      decoration.setDepth(995);
      
      // Subtle pulsing
      this.scene.tweens.add({
        targets: decoration,
        alpha: 0.3,
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: Phaser.Math.Between(0, 1000)
      });
    });
  }

  private createControlButtons(): void {
    this.createBackButton();
    this.createSettingsButton();
    this.createUIToggleButton();
  }

  private createBackButton(): void {
    const width = this.scene.cameras.main.width;
    
    this.backButton = this.scene.add.container(50, 50);
    this.backButton.setDepth(2000);
    
    // Button background
    const buttonBg = this.scene.add.graphics();
    buttonBg.fillGradientStyle(0xe74c3c, 0xc0392b, 0xe74c3c, 0xc0392b, 1);
    buttonBg.fillRoundedRect(-25, -25, 50, 50, 12);
    buttonBg.lineStyle(2, 0xff6b6b, 0.8);
    buttonBg.strokeRoundedRect(-25, -25, 50, 50, 12);
    this.backButton.add(buttonBg);
    
    // Glow effect
    const glow = this.scene.add.graphics();
    glow.fillStyle(0xe74c3c, 0.3);
    glow.fillRoundedRect(-30, -30, 60, 60, 15);
    this.backButton.add(glow);
    
    // Back arrow icon
    const arrow = this.scene.add.graphics();
    arrow.lineStyle(3, 0xffffff, 1);
    arrow.fillStyle(0xffffff, 1);
    arrow.beginPath();
    arrow.moveTo(5, 0);
    arrow.lineTo(-8, -8);
    arrow.lineTo(-5, -3);
    arrow.lineTo(-15, -3);
    arrow.lineTo(-15, 3);
    arrow.lineTo(-5, 3);
    arrow.lineTo(-8, 8);
    arrow.closePath();
    arrow.fillPath();
    this.backButton.add(arrow);
    
    // Interactive setup
    this.backButton.setInteractive(
      new Phaser.Geom.Rectangle(-25, -25, 50, 50),
      Phaser.Geom.Rectangle.Contains
    );
    
    this.backButton.on('pointerover', () => {
      this.scene.tweens.add({
        targets: this.backButton,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 200,
        ease: 'Back.easeOut'
      });
    });
    
    this.backButton.on('pointerout', () => {
      this.scene.tweens.add({
        targets: this.backButton,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: 'Back.easeOut'
      });
    });
    
    this.backButton.on('pointerdown', () => {
      // Confirmation dialog
      this.showExitConfirmation();
    });
    
    // Pulse animation
    this.scene.tweens.add({
      targets: glow,
      scaleX: 1.2,
      scaleY: 1.2,
      alpha: 0.1,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createSettingsButton(): void {
    const width = this.scene.cameras.main.width;
    
    this.settingsButton = this.scene.add.container(width - 50, 50);
    this.settingsButton.setDepth(2000);
    
    // Button background
    const buttonBg = this.scene.add.graphics();
    buttonBg.fillGradientStyle(0x95a5a6, 0x7f8c8d, 0x95a5a6, 0x7f8c8d, 1);
    buttonBg.fillRoundedRect(-25, -25, 50, 50, 12);
    buttonBg.lineStyle(2, 0xbdc3c7, 0.8);
    buttonBg.strokeRoundedRect(-25, -25, 50, 50, 12);
    this.settingsButton.add(buttonBg);
    
    // Glow effect
    const glow = this.scene.add.graphics();
    glow.fillStyle(0x95a5a6, 0.3);
    glow.fillRoundedRect(-30, -30, 60, 60, 15);
    this.settingsButton.add(glow);
    
    // Settings gear icon
    const gear = this.scene.add.graphics();
    gear.lineStyle(2, 0xffffff, 1);
    gear.strokeCircle(0, 0, 12);
    gear.strokeCircle(0, 0, 6);
    // Gear teeth
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      const x1 = Math.cos(angle) * 12;
      const y1 = Math.sin(angle) * 12;
      const x2 = Math.cos(angle) * 16;
      const y2 = Math.sin(angle) * 16;
      gear.lineBetween(x1, y1, x2, y2);
    }
    this.settingsButton.add(gear);
    
    // Interactive setup
    this.settingsButton.setInteractive(
      new Phaser.Geom.Rectangle(-25, -25, 50, 50),
      Phaser.Geom.Rectangle.Contains
    );
    
    this.settingsButton.on('pointerover', () => {
      this.scene.tweens.add({
        targets: this.settingsButton,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 200,
        ease: 'Back.easeOut'
      });
      
      this.scene.tweens.add({
        targets: gear,
        rotation: gear.rotation + Math.PI / 4,
        duration: 200,
        ease: 'Power2.easeOut'
      });
    });
    
    this.settingsButton.on('pointerout', () => {
      this.scene.tweens.add({
        targets: this.settingsButton,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: 'Back.easeOut'
      });
    });
    
    this.settingsButton.on('pointerdown', () => {
      this.showSettingsPanel();
    });
    
    // Slow rotation animation
    this.scene.tweens.add({
      targets: gear,
      rotation: Math.PI * 2,
      duration: 10000,
      repeat: -1,
      ease: 'Linear'
    });
  }

  private createUIToggleButton(): void {
    const width = this.scene.cameras.main.width;
    
    this.toggleUIButton = this.scene.add.container(width - 120, 50);
    this.toggleUIButton.setDepth(2000);
    
    // Button background
    const buttonBg = this.scene.add.graphics();
    buttonBg.fillGradientStyle(0x3498db, 0x2980b9, 0x3498db, 0x2980b9, 1);
    buttonBg.fillRoundedRect(-25, -25, 50, 50, 12);
    buttonBg.lineStyle(2, 0x74b9ff, 0.8);
    buttonBg.strokeRoundedRect(-25, -25, 50, 50, 12);
    this.toggleUIButton.add(buttonBg);
    
    // Toggle icon (eye)
    const eyeIcon = this.scene.add.graphics();
    eyeIcon.lineStyle(2, 0xffffff, 1);
    eyeIcon.strokeEllipse(0, 0, 16, 10);
    eyeIcon.fillStyle(0xffffff, 1);
    eyeIcon.fillCircle(0, 0, 4);
    this.toggleUIButton.add(eyeIcon);
    
    // Interactive setup
    this.toggleUIButton.setInteractive(
      new Phaser.Geom.Rectangle(-25, -25, 50, 50),
      Phaser.Geom.Rectangle.Contains
    );
    
    this.toggleUIButton.on('pointerover', () => {
      this.scene.tweens.add({
        targets: this.toggleUIButton,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 200,
        ease: 'Back.easeOut'
      });
    });
    
    this.toggleUIButton.on('pointerout', () => {
      this.scene.tweens.add({
        targets: this.toggleUIButton,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: 'Back.easeOut'
      });
    });
    
    this.toggleUIButton.on('pointerdown', () => {
      this.toggleUIVisibility();
    });
  }

  private showExitConfirmation(): void {
    const width = this.scene.cameras.main.width;
    const height = this.scene.cameras.main.height;
    
    // Overlay
    const overlay = this.scene.add.graphics();
    overlay.fillStyle(0x000000, 0.7);
    overlay.fillRect(0, 0, width, height);
    overlay.setDepth(3000);
    
    // Confirmation panel
    const panel = this.scene.add.graphics();
    panel.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 1);
    panel.fillRoundedRect(width/2 - 200, height/2 - 100, 400, 200, 15);
    panel.lineStyle(3, 0xe74c3c, 1);
    panel.strokeRoundedRect(width/2 - 200, height/2 - 100, 400, 200, 15);
    panel.setDepth(3001);
    
    // Title
    const title = this.scene.add.text(width/2, height/2 - 50, 'EXIT GAME?', {
      fontSize: '24px',
      fontFamily: 'Arial Black',
      color: '#e74c3c',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(3002);
    
    // Message
    const message = this.scene.add.text(width/2, height/2 - 10, 'Your progress will be saved automatically.', {
      fontSize: '16px',
      fontFamily: 'Arial',
      color: '#bdc3c7'
    }).setOrigin(0.5).setDepth(3002);
    
    // Buttons
    const yesBtn = this.createConfirmButton(width/2 - 80, height/2 + 40, 'YES', 0xe74c3c, () => {
      this.scene.scene.start('MainMenu');
    });
    yesBtn.setDepth(3002);
    
    const noBtn = this.createConfirmButton(width/2 + 80, height/2 + 40, 'NO', 0x27ae60, () => {
      overlay.destroy();
      panel.destroy();
      title.destroy();
      message.destroy();
      yesBtn.destroy();
      noBtn.destroy();
    });
    noBtn.setDepth(3002);
  }

  private createConfirmButton(x: number, y: number, text: string, color: number, callback: () => void): Phaser.GameObjects.Container {
    const button = this.scene.add.container(x, y);
    
    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 1);
    bg.fillRoundedRect(-40, -20, 80, 40, 8);
    bg.lineStyle(2, 0xffffff, 0.8);
    bg.strokeRoundedRect(-40, -20, 80, 40, 8);
    button.add(bg);
    
    const label = this.scene.add.text(0, 0, text, {
      fontSize: '16px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    button.add(label);
    
    button.setInteractive(
      new Phaser.Geom.Rectangle(-40, -20, 80, 40),
      Phaser.Geom.Rectangle.Contains
    );
    
    button.on('pointerover', () => {
      this.scene.tweens.add({
        targets: button,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 200
      });
    });
    
    button.on('pointerout', () => {
      this.scene.tweens.add({
        targets: button,
        scaleX: 1,
        scaleY: 1,
        duration: 200
      });
    });
    
    button.on('pointerdown', callback);
    
    return button;
  }

  private showSettingsPanel(): void {
    const width = this.scene.cameras.main.width;
    const height = this.scene.cameras.main.height;
    
    // Settings overlay
    const overlay = this.scene.add.graphics();
    overlay.fillStyle(0x000000, 0.7);
    overlay.fillRect(0, 0, width, height);
    overlay.setDepth(3000);
    
    // Settings panel
    const panel = this.scene.add.graphics();
    panel.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 1);
    panel.fillRoundedRect(width/2 - 250, height/2 - 200, 500, 400, 15);
    panel.lineStyle(3, 0x95a5a6, 1);
    panel.strokeRoundedRect(width/2 - 250, height/2 - 200, 500, 400, 15);
    panel.setDepth(3001);
    
    // Title
    const title = this.scene.add.text(width/2, height/2 - 160, 'GAME SETTINGS', {
      fontSize: '24px',
      fontFamily: 'Arial Black',
      color: '#95a5a6',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(3002);
    
    // Settings options
    this.createSettingsOptions(width/2, height/2, overlay, panel, title);
  }

  private createSettingsOptions(centerX: number, centerY: number, ...elementsToDestroy: Phaser.GameObjects.GameObject[]): void {
    const options = [
      { text: 'Audio Settings', action: () => console.log('Audio Settings') },
      { text: 'Graphics Quality', action: () => console.log('Graphics Quality') },
      { text: 'Controls', action: () => console.log('Controls') },
      { text: 'Resume Game', action: () => elementsToDestroy.forEach(el => el.destroy()) }
    ];
    
    options.forEach((option, index) => {
      const optionBtn = this.scene.add.text(
        centerX, 
        centerY - 60 + (index * 40), 
        option.text,
        {
          fontSize: '18px',
          fontFamily: 'Arial',
          color: '#ffffff',
          backgroundColor: '#34495e',
          padding: { x: 20, y: 10 }
        }
      ).setOrigin(0.5).setDepth(3002);
      
      optionBtn.setInteractive({ useHandCursor: true });
      
      optionBtn.on('pointerover', () => {
        optionBtn.setStyle({ backgroundColor: '#4a90e2' });
      });
      
      optionBtn.on('pointerout', () => {
        optionBtn.setStyle({ backgroundColor: '#34495e' });
      });
      
      optionBtn.on('pointerdown', option.action);
      
      elementsToDestroy.push(optionBtn);
    });
  }

  private toggleUIVisibility(): void {
    this.isUIMinimized = !this.isUIMinimized;
    
    const elementsToToggle = [
      ...this.uiElements.towerPanel,
      ...this.uiElements.sideElements,
      ...this.uiElements.equipmentStats
    ] ;
    
    if (this.isUIMinimized) {
      // Hide UI elements
      elementsToToggle.forEach(element => {
        this.scene.tweens.add({
          targets: element,
          alpha: 0,
          scaleX: 0.8,
          scaleY: 0.8,
          duration: 300,
          ease: 'Power2.easeOut',
          onComplete: () => {
            element.setActive(false);
          }
        });
      });
      
      // Update toggle button icon to show UI is hidden
      this.updateToggleButtonIcon(true);
    } else {
      // Show UI elements
      elementsToToggle.forEach(element => {
        element.setActive(true);
        this.scene.tweens.add({
          targets: element,
          alpha: 1,
          scaleX: 1,
          scaleY: 1,
          duration: 300,
          ease: 'Back.easeOut'
        });
      });
      
      this.updateToggleButtonIcon(false);
    }
  }

  private updateToggleButtonIcon(isHidden: boolean): void {
    if (!this.toggleUIButton) return;
    
    // Clear existing icon
    this.toggleUIButton.removeAll(true);
    
    // Recreate button with new icon
    const buttonBg = this.scene.add.graphics();
    buttonBg.fillGradientStyle(0x3498db, 0x2980b9, 0x3498db, 0x2980b9, 1);
    buttonBg.fillRoundedRect(-25, -25, 50, 50, 12);
    buttonBg.lineStyle(2, 0x74b9ff, 0.8);
    buttonBg.strokeRoundedRect(-25, -25, 50, 50, 12);
    this.toggleUIButton.add(buttonBg);
    
    if (isHidden) {
      // Closed eye icon
      const eyeIcon = this.scene.add.graphics();
      eyeIcon.lineStyle(2, 0xffffff, 1);
      eyeIcon.strokeEllipse(0, 0, 16, 10);
      // Crossed out
      eyeIcon.lineBetween(-12, -8, 12, 8);
      this.toggleUIButton.add(eyeIcon);
    } else {
      // Open eye icon
      const eyeIcon = this.scene.add.graphics();
      eyeIcon.lineStyle(2, 0xffffff, 1);
      eyeIcon.strokeEllipse(0, 0, 16, 10);
      eyeIcon.fillStyle(0xffffff, 1);
      eyeIcon.fillCircle(0, 0, 4);
      this.toggleUIButton.add(eyeIcon);
    }
  }

  // Hiện popup XP mỗi lần kill
  public showXPGain(x: number, y: number, amount: number): void {
    const xpText = this.scene.add.text(x, y, `+${amount} XP`, {
      fontSize: '18px',
      fontFamily: 'Arial Black',
      color: '#FFD700',
      fontStyle: 'bold',
      stroke: '#000',
      strokeThickness: 3
    }).setOrigin(0.5).setDepth(2000);
    this.scene.tweens.add({
      targets: xpText,
      y: y - 40,
      alpha: 0,
      duration: 900,
      ease: 'Power2',
      onComplete: () => xpText.destroy()
    });
  }
} 